import React, { useEffect, useState, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import SessionErrorBoundary from "./components/common/SessionErrorBoundary";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DevTools } from "./components/dev";
import PageLoader from "./components/common/PageLoader";
import landingPages from "./content/landingPages.json";
import "./index.css"; // Ensure Tailwind styles are imported

// Lazy load pages for code splitting
const HomePage = lazy(() => import("./pages/HomePage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const WelcomeBackPage = lazy(() => import("./pages/WelcomeBackPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const JoinSessionPage = lazy(() => import("./pages/JoinSessionPage"));
const HostSessionPage = lazy(() => import("./pages/HostSessionPage"));
const GuestSessionPage = lazy(() => import("./pages/GuestSessionPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const LogoSizeTester = lazy(() => import("./components/common/LogoSizeTester"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const BillingSuccessPage = lazy(() => import("./pages/BillingSuccessPage"));
const BillingCancelPage = lazy(() => import("./pages/BillingCancelPage"));
const VerificationSuccessPage = lazy(
  () => import("./pages/VerificationSuccessPage")
);
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));

// Helper component to redirect based on auth status
const AuthRedirector: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return <div>Loading...</div>; // Or a spinner
  }
  return <Navigate to={isAuthenticated ? "/dashboard" : "/"} replace />;
};

function App() {
  const [devToolsOpen, setDevToolsOpen] = useState(false);
  const isDev = import.meta.env.DEV;

  // Force dark mode for the application
  useEffect(() => {
    // Apply dark mode class to html element
    document.documentElement.classList.add("dark");

    // Set background color and text color directly on body as fallback
    document.body.style.backgroundColor = "#0f172a"; // Updated to match our new dark-bg color
    document.body.style.color = "#f1f5f9"; // Updated to match our new text-primary color
  }, []);

  // Keyboard shortcut for dev tools (Ctrl+Shift+D or Cmd+Shift+D)
  useEffect(() => {
    if (!isDev) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "D") {
        e.preventDefault();
        setDevToolsOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isDev]);

  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App bg-background text-text-primary">
          {isDev && (
            <DevTools
              isOpen={devToolsOpen}
              onClose={() => setDevToolsOpen(false)}
            />
          )}
          <Routes>
            <Route
              path="/"
              element={
                <Suspense fallback={<PageLoader />}>
                  <HomePage />
                </Suspense>
              }
            />
            <Route
              path="/login"
              element={
                <Suspense fallback={<PageLoader />}>
                  <LoginPage />
                </Suspense>
              }
            />
            <Route
              path="/register"
              element={
                <Suspense fallback={<PageLoader />}>
                  <RegisterPage />
                </Suspense>
              }
            />
            <Route
              path="/welcome-back"
              element={
                <Suspense fallback={<PageLoader />}>
                  <WelcomeBackPage />
                </Suspense>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ForgotPasswordPage />
                </Suspense>
              }
            />
            <Route
              path="/reset-password/:token"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ResetPasswordPage />
                </Suspense>
              }
            />
            <Route
              path="/join/:inviteToken"
              element={
                <Suspense fallback={<PageLoader />}>
                  <JoinSessionPage />
                </Suspense>
              }
            />
            <Route
              path="/about"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AboutPage />
                </Suspense>
              }
            />
            <Route
              path="/privacy"
              element={
                <Suspense fallback={<PageLoader />}>
                  <PrivacyPage />
                </Suspense>
              }
            />
            <Route
              path="/terms"
              element={
                <Suspense fallback={<PageLoader />}>
                  <TermsPage />
                </Suspense>
              }
            />
            {landingPages.map(({ path }) => (
              <Route
                key={path}
                path={path}
                element={
                  <Suspense fallback={<PageLoader />}>
                    <LandingPage />
                  </Suspense>
                }
              />
            ))}
            <Route
              path="/logo-test"
              element={
                <Suspense fallback={<PageLoader />}>
                  <LogoSizeTester />
                </Suspense>
              }
            />
            <Route
              path="/verification-success"
              element={
                <Suspense fallback={<PageLoader />}>
                  <VerificationSuccessPage />
                </Suspense>
              }
            />
            <Route
              path="/checkout"
              element={
                <Suspense fallback={<PageLoader />}>
                  <CheckoutPage />
                </Suspense>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <DashboardPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/session/:sessionId"
              element={
                <ProtectedRoute>
                  <SessionErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      <HostSessionPage />
                    </Suspense>
                  </SessionErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/play/:sessionId/:playerId"
              element={
                <SessionErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <GuestSessionPage />
                  </Suspense>
                </SessionErrorBoundary>
              }
            />
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <AccountPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing/success"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <BillingSuccessPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing/cancel"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <BillingCancelPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />

            {/* Fallback Route - Redirects based on auth */}
            <Route path="*" element={<AuthRedirector />} />
          </Routes>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
